import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { setIsModelTraining } from 'src/actions/workflow-builder/workflowBuilderActions';
import { PUSHER_ENCRYPTED_CHANNEL_PREFIX } from 'src/components/app/constants';
import { selectCurrentUser } from 'src/reducers/userReducer/userReducer';
import {
  AgentTestRunFinalizedEvent,
  AgentTestRunResultSummary,
} from 'src/services/solve-tests/types';
import { selectPusherSessionId } from 'src/slices/settings/settingsSlice';
import { useAppDispatch } from 'src/store/hooks';
import { ImprovedPolicyGenerationStatusChangeEvent } from 'src/types/workflowBuilderAPITypes';
import { getUserOrgCode } from 'src/utils/getUserOrgCode';
import { initializePusher } from 'src/utils/pusherUtils';

export enum PusherEvents {
  AB_TEST_CONFIGURATION_CHANGE = 'ab-test-configuration-change',
  ACTION_CONFIGURATION_CHANGE = 'action-configuration-change',
  AGENT_TEST_PREVIEW_RUN_FINALIZED = 'agent-test-preview-run-finalized',
  AGENT_TEST_RUN_FINALIZED = 'agent-test-run-finalized',
  AI_STUDIO_CONVERSATION_READY = 'ai-studio-conversation-ready',
  AI_STUDIO_CONVERSATION_STARTED = 'ai-studio-conversation-started',
  ASSIST_PREVIEW_LOG = 'assist-preview-log',
  AUTOFLOW_CONFIGURATION_CHANGE = 'autoflow-configuration-change',
  BACKGROUND_TASK_ERROR = 'background-task-error',
  BACKGROUND_TASK_SUCCESS = 'background-task-success',
  BROWSER_AGENT_LOG = 'browser-agent-log',
  CODE_EXECUTION_PREVIEW_RESULT = 'code-execution-preview-result',
  CONFIGURATION_CHANGE = 'configuration-change',
  DELETE_EMAIL_CONFIGURATION = 'delete-email-configuration',
  DELETE_INTENT = 'delete-intent',
  EMAIL_PREVIEW_LOG = 'email-preview-log',
  IMPROVED_POLICY_GENERATION_STATUS_CHANGE = 'improved-policy-generation-status-change',
  INTENT_STATUS = 'intent-status-change',
  INTENT_TEST_UPDATED = 'intent-test-updated',
  MODEL_TRAINING_STATUS_CHANGE = 'model-training-status-change',
  ORCHESTRATOR_PREVIEW_CANCELLED = 'orchestrator-preview-cancelled',
  ORCHESTRATOR_PREVIEW_COMPLETE = 'orchestrator-preview-complete',
  ORCHESTRATOR_PREVIEW_ERROR = 'orchestrator-preview-error',
  ORCHESTRATOR_PREVIEW_START = 'orchestrator-preview-start',
  ORCHESTRATOR_PREVIEW_STEP_COMPLETE = 'orchestrator-preview-step-complete',
  ORCHESTRATOR_PREVIEW_STEP_ERROR = 'orchestrator-preview-step-error',
  ORCHESTRATOR_PREVIEW_STEP_START = 'orchestrator-preview-step-start',
  ORCHESTRATOR_PREVIEW_TRIGGER_STEP_COMPLETE = 'orchestrator-preview-trigger-step-complete',
  ORCHESTRATOR_PREVIEW_TRIGGER_STEP_ERROR = 'orchestrator-preview-trigger-step-error',
  ORCHESTRATOR_PREVIEW_TRIGGER_STEP_START = 'orchestrator-preview-trigger-step-start',
  RELOAD_WORKFLOW = 'reload-workflow',
  UPDATE_INTENT = 'update-intent',
  WORKFLOW_ACTIVE_STATUS_CHANGE = 'workflow-active-status-change',
}

export const useDeleteEmailConfigurationEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: (configurationId: string, intentId: string) => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);
      channel.bind(
        PusherEvents.DELETE_EMAIL_CONFIGURATION,
        (data: {
          configuration_id: string;
          edit_session_uuid: string;
          intent_id: string;
        }) => {
          const { configuration_id, edit_session_uuid, intent_id } = data;
          if (edit_session_uuid !== sessionId) {
            onEvent(configuration_id, intent_id);
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent, sessionId]);
};

const useIntentStatusEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);
      channel.bind(
        PusherEvents.INTENT_STATUS,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent, sessionId]);
};

const useDeleteIntentEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);
      channel.bind(
        PusherEvents.DELETE_INTENT,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent, sessionId]);
};

const useUpdateIntentEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);
      channel.bind(
        PusherEvents.UPDATE_INTENT,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent, sessionId]);
};

const useReloadWorkflowEventSubscription = ({
  intentWorkflowId,
  onEvent,
  orgCode,
  version,
}: {
  intentWorkflowId: string | null;
  onEvent: () => void;
  orgCode?: string;
  version: number;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode && intentWorkflowId) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode};${intentWorkflowId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.RELOAD_WORKFLOW,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, intentWorkflowId, version, onEvent, sessionId]);
};

const useConfigurationChangeEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;configuration;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.CONFIGURATION_CHANGE,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent, sessionId]);
};

const useModelTrainingStatus = (orgCode?: string) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;preview;${orgCode};model-training`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.MODEL_TRAINING_STATUS_CHANGE,
        (data: { model_training_status: string }) => {
          const iframe = document.getElementById(
            'forethought-chat',
          ) as HTMLIFrameElement;
          const isTraining = data.model_training_status === 'training';
          dispatch(setIsModelTraining(isTraining));
          const solveOrigin = new URL(SOLVE_URL).origin;
          iframe?.contentWindow?.postMessage(
            {
              rawParams: {
                'data-is-model-training': isTraining ? 'true' : 'false',
              },
            },
            solveOrigin,
          );
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [dispatch, orgCode]);
};

const useCanvasActiveWorkflowStatusEventSubscription = ({
  intentWorkflowId,
  onEvent,
  orgCode,
  version,
}: {
  intentWorkflowId: string | null;
  onEvent: () => void;
  orgCode?: string;
  version: number;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode && intentWorkflowId) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;canvas;${orgCode};${intentWorkflowId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.WORKFLOW_ACTIVE_STATUS_CHANGE,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, version, onEvent, intentWorkflowId, sessionId]);
};

const useEmailPreviewLogEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: (
    log: {
      log_type: string;
      message?: string;
      metadata?: Record<string, unknown>;
      timestamp: string;
      title: string;
    },
    previewSessionId: string,
  ) => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;email-preview;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);

      const handler = (data: {
        log: {
          log_type: string;
          message?: string;
          metadata?: Record<string, unknown>;
          timestamp: string;
          title: string;
        };
        preview_session_id: string;
      }) => {
        onEvent(data.log, data.preview_session_id);
      };

      channel.bind(PusherEvents.EMAIL_PREVIEW_LOG, handler);

      return () => {
        channel.unbind(PusherEvents.EMAIL_PREVIEW_LOG, handler);
        window.pusher.unsubscribe(channelName);
      };
    }
  }, [orgCode, onEvent, sessionId]);
};

const useAssistPreviewLogEventSubscription = ({
  onEvent,
  orgCode,
}: {
  onEvent: (
    log: {
      log_type: string;
      message?: string;
      metadata?: Record<string, unknown>;
      timestamp: string;
      title: string;
    },
    previewSessionId: string,
  ) => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode && window.pusher) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}assist;preview;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);

      const handler = (data: {
        log: {
          log_type: string;
          message?: string;
          metadata?: Record<string, unknown>;
          timestamp: string;
          title: string;
        };
        preview_session_id: string;
      }) => {
        onEvent(data.log, data.preview_session_id);
      };

      channel.bind(PusherEvents.ASSIST_PREVIEW_LOG, handler);

      return () => {
        channel.unbind(PusherEvents.ASSIST_PREVIEW_LOG, handler);
        window.pusher?.unsubscribe(channelName);
      };
    }
  }, [orgCode, onEvent, sessionId]);
};

const useAgentTestRunFinalizedEventSubscription = ({
  onEvent,
}: {
  onEvent: (event: AgentTestRunFinalizedEvent) => void;
}) => {
  const orgCode = getUserOrgCode();
  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;agent-test-run;${orgCode}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.AGENT_TEST_RUN_FINALIZED,
        (data: AgentTestRunFinalizedEvent) => {
          onEvent(data);
        },
      );

      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, onEvent]);
};

const useAgentTestPreviewRunEventSubscription = ({
  conversationId,
  onEvent,
}: {
  conversationId: string;
  onEvent: (result: AgentTestRunResultSummary) => void;
}) => {
  const orgCode = getUserOrgCode();

  useEffect(() => {
    if (orgCode && conversationId) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;agent-test-preview-run;${orgCode};${conversationId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.AGENT_TEST_PREVIEW_RUN_FINALIZED,
        (data: { result: AgentTestRunResultSummary }) => {
          onEvent(data.result);
        },
      );

      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, conversationId, onEvent]);
};

export type OrchestratorPreviewLogStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'error';

export type OrchestratorPreviewLogType =
  | 'trigger'
  | 'action'
  | 'condition'
  | 'autoflow'
  | 'get_conversations'
  | 'loop'
  | 'set_context_variable'
  | 'triage'
  | 'voice'
  | 'code_interpreter';

export interface OrchestratorPreviewStepEvent {
  action_id?: string;
  call_id?: string;
  call_status?: string;
  channel?: string;
  code?: string;
  conversation_count?: number;
  /** Current loop iteration (1-indexed). */
  current_iteration?: number;
  date_range?: string;
  /** Context variable being iterated over. */
  data_set_cv_name?: string;
  display_name?: string;
  error_message?: string;
  failed_iterations?: number[];
  from_number?: string;
  input_context?: Record<string, unknown>;
  input_params?: Record<string, unknown>;
  intent_workflow_id?: string;
  internal_step_id?: string;
  internal_step_name?: string;
  internal_step_type?: string;
  log_type: OrchestratorPreviewLogType;
  loop_item?: unknown;
  loop_step_id?: string;
  message?: string;
  method?: string;
  model_name?: string;
  output_context?: Record<string, unknown>;
  output_cv_id?: string;
  output_params?: Record<string, unknown>;
  path_taken?: string;
  preview_session_id?: string;
  request_body?: Record<string, unknown>;
  response_status_code?: number;
  status: OrchestratorPreviewLogStatus;
  step_id: string;
  step_name: string;
  step_type: string;
  to_number?: string;
  tool_executions?: Array<{
    context_variable_name?: string | null;
    context_variable_value?: string | null;
    execution_type: 'tool' | 'set_context_variable';
    tool_input: Record<string, unknown>;
    tool_name: string;
    tool_output: Record<string, unknown>;
  }>;
  total_iterations?: number;
  transition_id?: string;
  trigger_type?: string;
  url_used?: string;
  voice_workflow_id?: string;
  voice_workflow_name?: string;
}

export interface OrchestratorPreviewErrorEvent {
  context?: Record<string, unknown>;
  error: string;
  preview_session_id: string;
  step_id?: string;
}

const useOrchestratorPreviewEventSubscription = ({
  onPreviewCancelledEvent,
  onPreviewCompleteEvent,
  onPreviewErrorEvent,
  onPreviewStartEvent,
  onStepCompleteEvent,
  onStepErrorEvent,
  onStepStartEvent,
  onTriggerStepCompleteEvent,
  onTriggerStepErrorEvent,
  onTriggerStepStartEvent,
  previewSessionId,
}: {
  onPreviewCancelledEvent?: () => void;
  onPreviewCompleteEvent: () => void;
  onPreviewErrorEvent?: (event: OrchestratorPreviewErrorEvent) => void;
  onPreviewStartEvent?: () => void;
  onStepCompleteEvent: (event: OrchestratorPreviewStepEvent) => void;
  onStepErrorEvent?: (event: OrchestratorPreviewStepEvent) => void;
  onStepStartEvent: (event: OrchestratorPreviewStepEvent) => void;
  onTriggerStepCompleteEvent?: (event: OrchestratorPreviewStepEvent) => void;
  onTriggerStepErrorEvent?: (event: OrchestratorPreviewStepEvent) => void;
  onTriggerStepStartEvent?: (event: OrchestratorPreviewStepEvent) => void;
  previewSessionId: string;
}) => {
  const orgCode = getUserOrgCode();

  useEffect(() => {
    if (orgCode && previewSessionId && window.pusher) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}orchestrator;preview;${orgCode};${previewSessionId}`;
      const channel = window.pusher.subscribe(channelName);

      // preview-level events
      channel.bind(PusherEvents.ORCHESTRATOR_PREVIEW_START, () => {
        onPreviewStartEvent?.();
      });

      channel.bind(PusherEvents.ORCHESTRATOR_PREVIEW_CANCELLED, () => {
        onPreviewCancelledEvent?.();
      });

      channel.bind(PusherEvents.ORCHESTRATOR_PREVIEW_COMPLETE, () => {
        onPreviewCompleteEvent();
      });

      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_ERROR,
        (data: OrchestratorPreviewErrorEvent) => {
          onPreviewErrorEvent?.(data);
        },
      );

      // trigger step events (for start node)
      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_START,
        (data: OrchestratorPreviewStepEvent) => {
          onTriggerStepStartEvent?.(data);
        },
      );

      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_COMPLETE,
        (data: OrchestratorPreviewStepEvent) => {
          onTriggerStepCompleteEvent?.(data);
        },
      );

      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_ERROR,
        (data: OrchestratorPreviewStepEvent) => {
          onTriggerStepErrorEvent?.(data);
        },
      );

      // regular step events
      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_STEP_START,
        (data: OrchestratorPreviewStepEvent) => {
          onStepStartEvent(data);
        },
      );

      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_STEP_COMPLETE,
        (data: OrchestratorPreviewStepEvent) => {
          onStepCompleteEvent(data);
        },
      );

      channel.bind(
        PusherEvents.ORCHESTRATOR_PREVIEW_STEP_ERROR,
        (data: OrchestratorPreviewStepEvent) => {
          onStepErrorEvent?.(data);
        },
      );

      return () => {
        channel.unbind('pusher:subscription_succeeded');
        channel.unbind('pusher:subscription_error');
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_START);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_CANCELLED);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_COMPLETE);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_ERROR);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_START);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_COMPLETE);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_TRIGGER_STEP_ERROR);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_STEP_START);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_STEP_COMPLETE);
        channel.unbind(PusherEvents.ORCHESTRATOR_PREVIEW_STEP_ERROR);
        window.pusher?.unsubscribe(channelName);
      };
    }
  }, [
    orgCode,
    previewSessionId,
    onPreviewCancelledEvent,
    onPreviewStartEvent,
    onPreviewCompleteEvent,
    onPreviewErrorEvent,
    onTriggerStepStartEvent,
    onTriggerStepCompleteEvent,
    onTriggerStepErrorEvent,
    onStepStartEvent,
    onStepCompleteEvent,
    onStepErrorEvent,
  ]);
};

const useActionConfigurationChangeEventSubscription = ({
  actionId,
  onEvent,
  orgCode,
}: {
  actionId?: string;
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;action-configuration;${orgCode};${actionId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.ACTION_CONFIGURATION_CHANGE,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, actionId, onEvent, sessionId]);
};

export type AutoflowPusherAction =
  | 'version_content_change'
  | 'version_history_change'
  | 'workflow_change'
  | 'child_workflow_change';

export type AutoflowVersionType = 'auto_saved' | 'saved' | 'published';

export type AutoflowVersionHistoryChangeType =
  | 'created'
  | 'deleted'
  | 'published'
  | 'renamed';

export interface AutoflowChangeEvent {
  action: AutoflowPusherAction;
  child_intent_workflow_id?: string;
  edit_session_uuid: string | null;
  history_change_type?: AutoflowVersionHistoryChangeType | null;
  intent_workflow_id: string;
  // On publish events: the source version the publish was created from. The
  // source draft's content mutates during publish, so it counts as affected.
  source_version?: number | null;
  source_version_type?: AutoflowVersionType | null;
  version?: number | null;
  version_type?: AutoflowVersionType | null;
}

const useAutoflowConfigurationChangeEventSubscription = ({
  intentWorkflowId,
  onEvent,
  orgCode,
}: {
  intentWorkflowId: string;
  onEvent: (event: AutoflowChangeEvent) => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode && intentWorkflowId) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;autoflow;${orgCode};${intentWorkflowId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.AUTOFLOW_CONFIGURATION_CHANGE,
        (data: AutoflowChangeEvent) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent(data);
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, intentWorkflowId, onEvent, sessionId]);
};

export interface CodeExecutionPreviewResultEvent {
  execution_id: string;
  status: 'success' | 'error';
  output_values: Record<string, string> | null;
  error_message: string | null;
}

const useCodeExecutionPreviewEventSubscription = ({
  executionId,
  onEvent,
}: {
  executionId: string | null;
  onEvent: (data: CodeExecutionPreviewResultEvent) => void;
}) => {
  const orgCode = getUserOrgCode();

  useEffect(() => {
    if (!orgCode || !executionId) {
      return;
    }

    const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;code-execution-preview;${orgCode};${executionId}`;
    const channel = window.pusher.subscribe(channelName);

    channel.bind(
      PusherEvents.CODE_EXECUTION_PREVIEW_RESULT,
      (data: CodeExecutionPreviewResultEvent) => {
        onEvent(data);
      },
    );

    return () => window.pusher.unsubscribe(channelName);
  }, [orgCode, executionId, onEvent]);
};

const useAbTestConfigurationChangeEventSubscription = ({
  abTestConfigId,
  onEvent,
  orgCode,
}: {
  abTestConfigId: string;
  onEvent: () => void;
  orgCode?: string;
}) => {
  const sessionId = useSelector(selectPusherSessionId);

  useEffect(() => {
    if (orgCode && abTestConfigId) {
      const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;ab-test;${orgCode};${abTestConfigId}`;
      const channel = window.pusher.subscribe(channelName);

      channel.bind(
        PusherEvents.AB_TEST_CONFIGURATION_CHANGE,
        (data: { edit_session_uuid: string }) => {
          if (data.edit_session_uuid !== sessionId) {
            onEvent();
          }
        },
      );
      return () => window.pusher.unsubscribe(channelName);
    }
  }, [orgCode, abTestConfigId, onEvent, sessionId]);
};

export interface AIStudioConversationReadyEvent {
  run_id: string;
  thread_id: string;
}

export interface AIStudioConversationStartedEvent {
  run_id: string;
  thread_id: string;
}

/**
 * Workflow-scoped subscription for improved-policy generation outcomes.
 * The BE fires on terminal transitions only (completed / no_improvement /
 * failed) and the payload is deliberately minimal — the handler should
 * refetch the latest-job row rather than render from the event.
 *
 * Unlike the sibling hooks there is NO edit-session-uuid echo filter: the
 * event carries none, and the tab that triggered the run is exactly the one
 * that must hear the outcome. Repeat notifications for the same run are
 * normal (worker retries, sweeper recovery) — de-duplicate in the handler.
 */
const useImprovedPolicyGenerationStatusEventSubscription = ({
  enabled,
  intentWorkflowId,
  onEvent,
}: {
  enabled: boolean;
  intentWorkflowId: string | null;
  onEvent: (event: ImprovedPolicyGenerationStatusChangeEvent) => void;
}) => {
  // Read the user slice reactively so the effect re-runs once Cognito
  // finishes populating the store (see useListenAIStudioConversationEvents).
  const user = useSelector(selectCurrentUser);
  const orgCode = user.orgCode;

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !orgCode || !intentWorkflowId) {
      return;
    }
    const pusher = initializePusher();
    const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;improved-policy-generation;${orgCode};${intentWorkflowId}`;
    const channel = pusher.subscribe(channelName);
    channel.bind('pusher:subscription_error', (err: unknown) => {
      console.warn(
        'Improved-policy generation pusher subscription error',
        channelName,
        err,
      );
    });
    channel.bind(
      PusherEvents.IMPROVED_POLICY_GENERATION_STATUS_CHANGE,
      (data: ImprovedPolicyGenerationStatusChangeEvent) => {
        onEventRef.current(data);
      },
    );
    return () => {
      channel.unbind('pusher:subscription_error');
      channel.unbind(PusherEvents.IMPROVED_POLICY_GENERATION_STATUS_CHANGE);
      pusher.unsubscribe(channelName);
    };
  }, [enabled, orgCode, intentWorkflowId]);
};

const useListenAIStudioConversationEvents = ({
  onReady,
  onStarted,
}: {
  onReady?: (event: AIStudioConversationReadyEvent) => void;
  onStarted?: (event: AIStudioConversationStartedEvent) => void;
}) => {
  // Read the user slice reactively so the effect re-runs once Cognito
  // finishes populating the store. `getUserOrgCode()` / `getUserSub()` read
  // imperatively and wouldn't trigger a re-render.
  const user = useSelector(selectCurrentUser);
  const orgCode = user.orgCode;
  const userSub = user.user?.user?.sub;

  // Refs keep the handlers in sync with the latest callbacks without
  // forcing a resubscribe on every render.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;

  useEffect(() => {
    if (!orgCode || !userSub) {
      return;
    }
    // Defense-in-depth: the module-level init in `src/components/app/app.tsx`
    // normally sets `window.pusher` before any subscriber effect runs, but
    // call it here too. Idempotent — no-op if already initialized.
    const pusher = initializePusher();
    const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}ai-studio;notifications;${orgCode};${userSub}`;
    const channel = pusher.subscribe(channelName);
    channel.bind('pusher:subscription_error', (err: unknown) => {
      console.warn('AI Studio pusher subscription error', channelName, err);
    });
    channel.bind(
      PusherEvents.AI_STUDIO_CONVERSATION_READY,
      (data: AIStudioConversationReadyEvent) => {
        onReadyRef.current?.(data);
      },
    );
    channel.bind(
      PusherEvents.AI_STUDIO_CONVERSATION_STARTED,
      (data: AIStudioConversationStartedEvent) => {
        onStartedRef.current?.(data);
      },
    );
    return () => {
      channel.unbind('pusher:subscription_error');
      channel.unbind(PusherEvents.AI_STUDIO_CONVERSATION_READY);
      channel.unbind(PusherEvents.AI_STUDIO_CONVERSATION_STARTED);
      pusher.unsubscribe(channelName);
    };
  }, [orgCode, userSub]);
};

export {
  useAbTestConfigurationChangeEventSubscription,
  useActionConfigurationChangeEventSubscription,
  useAgentTestPreviewRunEventSubscription,
  useAgentTestRunFinalizedEventSubscription,
  useAssistPreviewLogEventSubscription,
  useAutoflowConfigurationChangeEventSubscription,
  useCanvasActiveWorkflowStatusEventSubscription,
  useCodeExecutionPreviewEventSubscription,
  useConfigurationChangeEventSubscription,
  useDeleteIntentEventSubscription,
  useEmailPreviewLogEventSubscription,
  useImprovedPolicyGenerationStatusEventSubscription,
  useIntentStatusEventSubscription,
  useListenAIStudioConversationEvents,
  useModelTrainingStatus,
  useOrchestratorPreviewEventSubscription,
  useReloadWorkflowEventSubscription,
  useUpdateIntentEventSubscription,
};
